import * as ko from 'knockout';
import LZString from 'lz-string';
import { ppFetch } from '../shared/pp-fetch';
import { DateTime } from 'luxon';
import { SchedulePickerBase } from '../shared/schedule-picker-base';
import { NflTeam } from './models/team';
import { NflGame } from './models/game';
import { NflWeek } from './models/week';
import { GameType } from '../shared/models/game-type';
import { DraftTrade } from '../shared/models/draft-trade';
import { SelectionType } from '../shared/enums/selection-type';
import { RankMethod } from '../shared/enums/rank-method';
import { Conferences, CommonGamesMethodology } from './enums';
import { Enumerable } from '../shared/linq';
import { readConfig, getApimBaseUrl, getWebAppBaseUrl } from '../shared/config';
import { userConfigManager } from '../shared/user-config-manager';
import type { IGame } from '../shared/interfaces/IGame';
import type { ITeam } from '../shared/interfaces/ITeam';
import type { IWeek } from '../shared/interfaces/IWeek';
import type { NflTiebreakerTeam, GameResult } from '../shared/tiebreaker/types';
import type { TiebreakerRuleSetSnapshot } from '../shared/league-structure/types';
import {
  nflConferenceRanker,
  nflConferencePickTopResult,
  nflDivisionRanker,
  nflDraftOrder,
  nflWildcardRanker,
  type NflTiebreakerOptions,
} from './tiebreaker/nfl-tiebreaker';
import { extractNflTiebreakerRuleSet } from './tiebreaker/rule-set-extractor';
import { ArrayGameResultsLookup } from '../shared/tiebreaker/game-results-lookup';
import {
  toNflTiebreakerTeam,
  buildTeamObsMap,
  applyTiebreakerResult,
  reorderTeamObs,
} from '../shared/tiebreaker/adapters/ko-adapter';
import * as Information from './schedule-info';
import * as BracketGenerator from './bracket-generator';
import { mapDictionaryToArray, rankSort } from '../shared/helpers';

// Re-export for entry point use
export { NflApp };

class NflApp extends SchedulePickerBase {
  ConferenceTeams: ConferenceTeamDictionary;
  Teams: { key: string; value: Record<string, Record<string, KnockoutObservable<ITeam>>> }[];
  Dict: TeamDictionary;
  AFCTop: KnockoutObservable<any>;
  NFCTop: KnockoutObservable<any>;
  RNflStyles: KnockoutObservable<boolean>;
  Warning: KnockoutObservable<string>;
  Info: KnockoutObservable<string>;
  SkipDivisionRank: KnockoutObservable<boolean>;
  SeasonYearEnd = 2021;
  HtmlExport: KnockoutObservable<any>;
  Export: KnockoutObservable<any>;
  TeamsOnBye: KnockoutComputed<any>;
  ShowDivision: KnockoutObservable<boolean>;
  ShowSOV: KnockoutObservable<boolean>;
  CommonGamesHandling: KnockoutObservable<CommonGamesMethodology>;
  TreatNonPlayoffTeamsLikeWildcards: KnockoutObservable<boolean>;
  DraftOrder: KnockoutObservableArray<KnockoutObservable<NflTeam>>;
  HideStatus: KnockoutObservable<boolean>;
  IsDraftOrderRanking: KnockoutObservable<boolean>;
  Version: number;
  NumGames: KnockoutObservable<number>;
  ShowSaveString: KnockoutObservable<boolean>;
  DraftTrades: KnockoutObservableArray<DraftTrade>;
  DraftTradesMap: DraftTradeMap = {};
  static postSeasonWeeks: number = 4;
  PostSeasonVersion = 1;
  private tiebreakerRuleSet?: TiebreakerRuleSetSnapshot;

  constructor() {
    const config = readConfig();
    super('football', config);
    super.startComputing();
    var self = this;
    self.DraftTrades = ko.observableArray<any>([]);
    self.ShowSaveString = ko.observable(!!NflApp.getQueryVariable('showString'));
    self.Hash = ko.observable(window.location.hash);
    self.NumGames = ko.observable(256);
    var configVars = NflApp.queryVariableToDictionary('c');
    self.IsDraftOrderRanking = ko.observable(false);
    self.ShowDivision = ko.observable(userConfigManager.UserConfig.NFL.DivToggle);
    self.NumberOfTeams(32);
    self.MaxWeek(17);
    self.Warning = ko.observable<string>('');
    self.Info = ko.observable<string>('');
    self.DraftOrder = ko.observableArray<KnockoutObservable<NflTeam>>([]);
    if (
      configVars.overrides &&
      (configVars['tnptlw'] != userConfigManager.UserConfig.NFL.TreatNonPlayoffTeamsLikeWildcards ||
        configVars['cgm'] != userConfigManager.UserConfig.NFL.CommonGamesMethodology)
    ) {
      self.Info(
        'Please note that this schedule was loaded with settings different from your saved ones. Your saved settings have not been lost, but the overrides will be used for the duration of this session.'
      );
    }
    self.PrimeTimeList = ko.observableArray<any>([]);
    self.HtmlExport = ko.observable('');
    self.Export = ko.observable('');
    self.EmptySeasonString = 'Aw18ZXTt-DFOS1b0ckA';
    self.TreatNonPlayoffTeamsLikeWildcards = ko.observable(
      (configVars['tnptlw'] as boolean) ||
        userConfigManager.UserConfig.NFL.TreatNonPlayoffTeamsLikeWildcards
    );
    self.TreatNonPlayoffTeamsLikeWildcards.subscribe((newVal: boolean) => {
      if (newVal != userConfigManager.UserConfig.NFL.TreatNonPlayoffTeamsLikeWildcards) {
        userConfigManager.UserConfig.NFL.TreatNonPlayoffTeamsLikeWildcards = newVal;
        userConfigManager.saveStorage();
        self.UpdateStats(-1, true);
      }
    });

    self.HideStatus = ko.observable(userConfigManager.UserConfig.NFL.HideStatus);
    self.HideStatus.subscribe((newVal: boolean) => {
      if (newVal != userConfigManager.UserConfig.NFL.HideStatus) {
        userConfigManager.UserConfig.NFL.HideStatus = newVal;
        userConfigManager.saveStorage();
      }
    });
    self.ShowSOV = ko.observable(userConfigManager.UserConfig.NFL.ShowSOV);
    self.ShowSOV.subscribe((newVal: any) => {
      if (newVal != userConfigManager.UserConfig.NFL.ShowSOV) {
        userConfigManager.UserConfig.NFL.ShowSOV = newVal;
        userConfigManager.saveStorage();
      }
    });
    self.CommonGamesHandling = ko.observable(
      (configVars['cgm'] as CommonGamesMethodology) ||
        userConfigManager.UserConfig.NFL.CommonGamesMethodology
    );
    self.CommonGamesHandling.subscribe((newVal: any) => {
      let maxWeek = this.MaxWeek();
      if (typeof newVal === 'string') {
        self.CommonGamesHandling(parseInt(newVal, 10));
        return;
      }
      if (newVal != userConfigManager.UserConfig.NFL.CommonGamesMethodology) {
        userConfigManager.UserConfig.NFL.CommonGamesMethodology = newVal;
        userConfigManager.saveStorage();
        if (
          self.Weeks[maxWeek + 2][0].games().length == 0 ||
          self.Weeks[maxWeek + 2][2].games().length == 0
        ) {
          self.UpdateStats(-1, true);
        }
      }
    });
    self.SaveString.subscribe(() => {
      self.setExport();
      self.setUrlRoute();
    });

    const leagueGuid = config.leagueGuid;
    // Expose leagueGuid globally — KO view bindings reference it as a bare variable
    (window as any).leagueGuid = leagueGuid;
    const sportId = config.sportId;
    const officialLeagueGuids = config.officialLeagueGuids;
    let isOfficialLeague =
      officialLeagueGuids != null &&
      officialLeagueGuids.indexOf(leagueGuid.toString().toUpperCase()) !== -1;
    if (isOfficialLeague) {
      ppFetch<any>({
        url: `${getApimBaseUrl()}/LeagueEngine/leagues/${sportId}/${leagueGuid}/official`,
        method: 'GET',
      })
        .then((result: any) => {
          self.tiebreakerRuleSet = extractNflTiebreakerRuleSet(result);
          self.SeasonYearEnd = result.league.superbowlYear;
          self.LeagueName(result.league.leagueName);
          self.CurrentSeasonString(result.league.currentWeekString);
          self.PostSeasonVersion = result.league.features.leagueVersion;

          var dict: TeamDictionary = {};
          var teamObj: KnockoutObservable<ITeam>;
          self.ConferenceTeams = {} as ConferenceTeamDictionary;
          self.ConferenceTeams['AFC'] = {} as Record<string, KnockoutObservable<ITeam>>;
          dict['AFC'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
          ko.utils.arrayForEach(
            result.teams.filter((p: ITeamDataV2) => p.conference == 'AFC'),
            (team: ITeamDataV2) => {
              dict['AFC'][team.division] = dict['AFC'][team.division] || {};
              teamObj = ko.observable(
                new NflTeam(
                  team.teamAbbreviation,
                  team.regionName,
                  team.teamName,
                  team.conference,
                  team.division,
                  team.teamId,
                  self.PostSeasonVersion,
                  team.teamLogo
                )
              );
              dict['AFC'][team.division][team.teamAbbreviation] = teamObj;
              self.ConferenceTeams['AFC'][team.teamAbbreviation] = teamObj;
            }
          );
          dict['NFC'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
          self.ConferenceTeams['NFC'] = {} as Record<string, KnockoutObservable<ITeam>>;
          ko.utils.arrayForEach(
            result.teams.filter((p: ITeamDataV2) => p.conference == 'NFC'),
            (team: ITeamDataV2) => {
              dict['NFC'][team.division] = dict['NFC'][team.division] || {};
              teamObj = ko.observable(
                new NflTeam(
                  team.teamAbbreviation,
                  team.regionName,
                  team.teamName,
                  team.conference,
                  team.division,
                  team.teamId,
                  self.PostSeasonVersion,
                  team.teamLogo
                )
              );
              dict['NFC'][team.division][team.teamAbbreviation] = teamObj;
              self.ConferenceTeams['NFC'][team.teamAbbreviation] = teamObj;
            }
          );
          self.Dict = dict;
          self.Teams = mapDictionaryToArray(self.Dict);

          self.MaxWeek(
            Enumerable.From(result.league.games).Max((p: any) => p.week)
          );
          self.TotalWeeks = self.MaxWeek() + 4;
          self.Weeks = Information.GetScheduleV2(
            self.Dict,
            result.league.games,
            self.MaxWeek(),
            result.teams
          );
          self.Version = 2;
          for (let weekIdx in self.Weeks) {
            let week = self.Weeks[weekIdx];
            var games = week[0].games().concat(week[1].games());
            if (week.length == 3) {
              games = games.concat(week[2].games());
            }
            for (let gameObs of games) {
              let game = gameObs();
              self.GameScheduleGameMap[game.gameScheduleNum()] = gameObs;
            }
          }

          self.SelectedWeek({ key: 1, value: self.Weeks[1] });
          self.NumGames(result.league.games.length);

          self.Warning.subscribe((_newVal) => {
            // Bootstrap alert() removed; warnings are displayed via KO binding
          });
          self.AFCTop = ko.observable([]);
          self.NFCTop = ko.observable([]);

          self.RNflStyles = ko.observable<boolean>(false);
          self.RNflStyles.subscribe(() => {
            self.setExport();
          });
          self.SkipDivisionRank = ko.observable<boolean>(false);
          self.init([]);

          if (result.league.draftTrades) {
            result.league.draftTrades.map((trade: IDraftTradeDataV2) => {
              let givingTeamRaw = result.teams.filter(
                (p: ITeamDataV2) => p.teamId == trade.givingTeamId
              )[0];
              let receivingTeamRaw = result.teams.filter(
                (p: ITeamDataV2) => p.teamId == trade.receivingTeamId
              )[0];
              let receivingTeam = self
                .AllTeams()
                .filter((teamObs) => teamObs().TeamID == receivingTeamRaw.teamId)[0];
              let givingTeam = self
                .AllTeams()
                .filter((teamObs) => teamObs().TeamID == givingTeamRaw.teamId)[0];
              self.DraftTradesMap[givingTeamRaw.teamAbbreviation] = receivingTeam();
              self.DraftTrades.push(
                new DraftTrade({
                  GivingTeam: givingTeam(),
                  ReceivingTeam: receivingTeam(),
                })
              );
            });
          }
          if (self.Hash()) {
            try {
              var team = self.findTeam(self.Hash().slice(1))();
              self.showTeamStats(team);
              self.IsTeamSelected(true);
            } catch (ex) {
              console.warn(ex);
            }
          }
          self.UrlString.subscribe((newString) => {
            if (newString) {
              newString = newString.replace(/\%20/gi, '+');
              var decompress = LZString.decompressFromEncodedURIComponent(newString);
              if (decompress) {
                self.load(decompress);
                if (
                  self.Weeks[self.TotalWeeks] &&
                  self.Weeks[self.TotalWeeks][1].GamesComputed().length > 0 &&
                  self.Weeks[self.TotalWeeks][1].GamesComputed()[0]().selectionMade()
                ) {
                  self.SelectedWeek({
                    key: self.TotalWeeks,
                    value: self.Weeks[self.TotalWeeks],
                  });
                }
              }
              self.setUrlRoute();
            } else {
              self.setUrlRoute(true);
            }
          });

          self.TeamsOnBye = ko.computed(() => {
            if (
              self.SelectedWeek() &&
              self.SelectedWeek().key &&
              self.AllTeams().length == self.NumberOfTeams()
            ) {
              if (+self.SelectedWeek().key == self.MaxWeek() + 1) {
                if (self.PostSeasonVersion == 2) {
                  return [self.AFCTop()[0], self.NFCTop()[0]];
                } else if (self.PostSeasonVersion == 1) {
                  return [
                    self.AFCTop()[0],
                    self.AFCTop()[1],
                    self.NFCTop()[0],
                    self.NFCTop()[1],
                  ];
                }
              } else if (+self.SelectedWeek().key > self.MaxWeek()) return [];
              var teamsNotSeen = [];
              for (var i = 1; i <= self.NumberOfTeams(); i++) {
                teamsNotSeen.push(i);
              }
              var id;
              for (id of self.SelectedWeek().value[0].TeamsInWeek()) {
                if (teamsNotSeen.indexOf(id) != -1) {
                  teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
                }
              }
              for (id of self.SelectedWeek().value[1].TeamsInWeek()) {
                if (teamsNotSeen.indexOf(id) != -1) {
                  teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
                }
              }
              for (id of self.SelectedWeek().value[2].TeamsInWeek()) {
                if (teamsNotSeen.indexOf(id) != -1) {
                  teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
                }
              }
              var distinctTeams = ko.utils.arrayGetDistinctValues(teamsNotSeen);
              return Enumerable.From(
                distinctTeams.map((id) => {
                  return self.findTeamByID(id);
                })
              )
                .OrderBy((p: KnockoutObservable<NflTeam>) => p && p().TeamNameObs())
                .ToArray();
            }
          });

          if (self.LoadedWatchlist()) {
            self.UrlString(self.LoadedWatchlist().SaveString());
          } else if (config.draftOrderPage) {
            self.IsComputing(false);
            self.setCurrentSeason(true);
          } else {
            self.UrlString(
              SchedulePickerBase.getQueryVariable('L') ||
                '' ||
                localStorage.getItem(leagueGuid) ||
                self.CurrentSeasonString()
            );
          }

          document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));
          const loadingElOfficial = document.getElementById('loading');
          if (loadingElOfficial) loadingElOfficial.style.display = 'none';
          setTimeout(() => {
            const navbars = document.querySelectorAll('.navbar');
            const navbar0 = navbars[0] as HTMLElement;
            const navbar1 = navbars[1] as HTMLElement;
          }, 80);
          ko.applyBindings(self, document.getElementById('content'));

          const navbarsForPadding = document.querySelectorAll('.navbar');
          const nb0 = navbarsForPadding[0] as HTMLElement;
          const nb1 = navbarsForPadding[1] as HTMLElement;
          document.body.style.paddingTop = ((nb0 ? nb0.offsetHeight : 0) + (nb1 ? nb1.offsetHeight : 0)) + 'px';
          // Bootstrap popover() removed — use native tooltips or data-bs-toggle if needed

          self.setGameScheduleNumbers(272);

          self.setExport();
          super.initTeamSubscribers();
          super.finishComputing();
        })
        .catch((err: unknown) => {
          console.error('NFL schedule-picker load error (official):', err);
          const loadingEl = document.getElementById('loading');
          if (loadingEl) loadingEl.textContent = 'An error occurred';
        });
    } else {
      ppFetch<IAjaxResult<any>>({
        url: `/football/CustomLeague/GetLeague/?leagueGuid=${leagueGuid}`,
        method: 'GET',
      })
        .then((ajaxResult: IAjaxResult<any>) => {
          if (!ajaxResult.IsRequestSuccessful) {
            const loadingEl = document.getElementById('loading');
            if (loadingEl) loadingEl.textContent = ajaxResult.Message;
            return;
          }
          if (
            !ajaxResult.Model.League.IsCustomLeague &&
            ajaxResult.Model.League.Games.length == 0
          ) {
            window.location.href = '/Football/NFL/Soon';
            return;
          }
          if (ajaxResult.Model.League.DraftTrades) {
            ajaxResult.Model.League.DraftTrades.map((trade: IDraftTradeDataV1) => {
              self.DraftTradesMap[trade.GivingTeam.TeamAbbreviation] = trade.ReceivingTeam;
              self.DraftTrades.push(new DraftTrade(trade));
            });
          }
          self.tiebreakerRuleSet = extractNflTiebreakerRuleSet(ajaxResult);
          if (ajaxResult.Model.League.SuperbowlYear) {
            self.SeasonYearEnd = ajaxResult.Model.League.SuperbowlYear;
          }
          if (ajaxResult.Model.League.CurrentWeekString) {
            self.CurrentSeasonString(ajaxResult.Model.League.CurrentWeekString);
          }
          self.LeagueName(ajaxResult.Model.League.LeagueName);
          self.IsCustomLeague(ajaxResult.Model.League.IsCustomLeague);
          if (DateTime.fromISO(ajaxResult.Model.League.CreatedOn) >= DateTime.fromISO('2020-03-15T00:00:00.000')) {
            self.PostSeasonVersion = 2;
          }
          var dict: TeamDictionary = {};
          var teamObj: KnockoutObservable<ITeam>;
          self.ConferenceTeams = {} as ConferenceTeamDictionary;
          self.ConferenceTeams['AFC'] = {} as Record<string, KnockoutObservable<ITeam>>;
          dict['AFC'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
          ko.utils.arrayForEach(ajaxResult.Model.Teams[0], (team: ITeamData) => {
            dict['AFC'][team.Division] = dict['AFC'][team.Division] || {};
            teamObj = ko.observable(
              new NflTeam(
                team.TeamAbbreviation,
                team.RegionName,
                team.TeamName,
                team.Conference,
                team.Division,
                team.TeamID,
                self.PostSeasonVersion,
                team.TeamLogo
              )
            );
            dict['AFC'][team.Division][team.TeamAbbreviation] = teamObj;
            self.ConferenceTeams['AFC'][team.TeamAbbreviation] = teamObj;
          });
          dict['NFC'] = {} as Record<string, Record<string, KnockoutObservable<ITeam>>>;
          self.ConferenceTeams['NFC'] = {} as Record<string, KnockoutObservable<ITeam>>;
          ko.utils.arrayForEach(ajaxResult.Model.Teams[1], (team: ITeamData) => {
            dict['NFC'][team.Division] = dict['NFC'][team.Division] || {};
            teamObj = ko.observable(
              new NflTeam(
                team.TeamAbbreviation,
                team.RegionName,
                team.TeamName,
                team.Conference,
                team.Division,
                team.TeamID,
                self.PostSeasonVersion,
                team.TeamLogo
              )
            );
            dict['NFC'][team.Division][team.TeamAbbreviation] = teamObj;
            self.ConferenceTeams['NFC'][team.TeamAbbreviation] = teamObj;
          });
          ko.utils.arrayForEach(ajaxResult.Model.PrimeTimes, (item: any) => {
            self.PrimeTimeList.push(new GameType(item));
          });
          self.Dict = dict;
          self.Teams = mapDictionaryToArray(self.Dict);
          self.MaxWeek(
            Enumerable.From(ajaxResult.Model.League.Games).Max((p: IGameData) => p.Week)
          );
          self.TotalWeeks = self.MaxWeek() + 4;
          self.Weeks = Information.GetSchedule(
            self.Dict,
            ajaxResult.Model.League.Games,
            self.MaxWeek()
          );

          if (
            Enumerable.From(ajaxResult.Model.League.Games).All(
              (p: IGameData) => p.GameScheduleNum != null
            )
          ) {
            self.Version = 2;
            for (let weekIdx in self.Weeks) {
              let week = self.Weeks[weekIdx];
              var games = week[0].games().concat(week[1].games());
              if (week.length == 3) {
                games = games.concat(week[2].games());
              }
              for (let gameObs of games) {
                let game = gameObs();
                self.GameScheduleGameMap[game.gameScheduleNum()] = gameObs;
              }
            }
          } else {
            self.Version = 1;
          }

          self.SelectedWeek({ key: 1, value: self.Weeks[1] });
          self.NumGames(ajaxResult.Model.League.Games.length);

          self.Warning.subscribe((_newVal) => {
            // Bootstrap alert() removed; warnings are displayed via KO binding
          });
          self.AFCTop = ko.observable([]);
          self.NFCTop = ko.observable([]);

          self.RNflStyles = ko.observable<boolean>(false);
          self.RNflStyles.subscribe(() => {
            self.setExport();
          });
          self.SkipDivisionRank = ko.observable<boolean>(false);
          self.init(ajaxResult.Model.ReplaceTeams);
          if (self.Hash()) {
            try {
              var team = self.findTeam(self.Hash().slice(1))();
              self.showTeamStats(team);
              self.IsTeamSelected(true);
            } catch (ex) {
              console.warn(ex);
            }
          }
          self.UrlString.subscribe((newString) => {
            if (newString) {
              newString = newString.replace(/\%20/gi, '+');
              var decompress = LZString.decompressFromEncodedURIComponent(newString);
              if (decompress) {
                self.load(decompress);
                if (
                  self.Weeks[self.TotalWeeks] &&
                  self.Weeks[self.TotalWeeks][1].GamesComputed().length > 0 &&
                  self.Weeks[self.TotalWeeks][1].GamesComputed()[0]().selectionMade()
                ) {
                  self.SelectedWeek({
                    key: self.TotalWeeks,
                    value: self.Weeks[self.TotalWeeks],
                  });
                }
              }
              self.setUrlRoute();
            } else {
              self.setUrlRoute(true);
            }
          });

          self.TeamsOnBye = ko.computed(() => {
            if (
              self.SelectedWeek() &&
              self.SelectedWeek().key &&
              self.AllTeams().length == self.NumberOfTeams()
            ) {
              if (+self.SelectedWeek().key == self.MaxWeek() + 1) {
                if (self.PostSeasonVersion == 2) {
                  return [self.AFCTop()[0], self.NFCTop()[0]];
                } else if (self.PostSeasonVersion == 1) {
                  return [
                    self.AFCTop()[0],
                    self.AFCTop()[1],
                    self.NFCTop()[0],
                    self.NFCTop()[1],
                  ];
                }
              } else if (+self.SelectedWeek().key > self.MaxWeek()) return [];
              var teamsNotSeen = [];
              for (var i = 1; i <= self.NumberOfTeams(); i++) {
                teamsNotSeen.push(i);
              }
              var id;
              for (id of self.SelectedWeek().value[0].TeamsInWeek()) {
                if (teamsNotSeen.indexOf(id) != -1) {
                  teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
                }
              }
              for (id of self.SelectedWeek().value[1].TeamsInWeek()) {
                if (teamsNotSeen.indexOf(id) != -1) {
                  teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
                }
              }
              for (id of self.SelectedWeek().value[2].TeamsInWeek()) {
                if (teamsNotSeen.indexOf(id) != -1) {
                  teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
                }
              }
              var distinctTeams = ko.utils.arrayGetDistinctValues(teamsNotSeen);
              return Enumerable.From(
                distinctTeams.map((id) => {
                  return self.findTeamByID(id);
                })
              )
                .OrderBy((p: KnockoutObservable<NflTeam>) => p && p().TeamNameObs())
                .ToArray();
            }
          });

          if (self.LoadedWatchlist()) {
            self.UrlString(self.LoadedWatchlist().SaveString());
          } else if (config.draftOrderPage) {
            self.IsComputing(false);
            self.setCurrentSeason(true);
          } else {
            self.UrlString(
              SchedulePickerBase.getQueryVariable('L') ||
                '' ||
                localStorage.getItem(leagueGuid) ||
                self.CurrentSeasonString()
            );
          }

          document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));
          const loadingElCustom = document.getElementById('loading');
          if (loadingElCustom) loadingElCustom.style.display = 'none';
          setTimeout(() => {
            const navbarsC = document.querySelectorAll('.navbar');
            const navbar0C = navbarsC[0] as HTMLElement;
            const navbar1C = navbarsC[1] as HTMLElement;
          }, 80);
          ko.applyBindings(self, document.getElementById('content'));

          const navbarsC2 = document.querySelectorAll('.navbar');
          const nb0C = navbarsC2[0] as HTMLElement;
          const nb1C = navbarsC2[1] as HTMLElement;
          document.body.style.paddingTop = ((nb0C ? nb0C.offsetHeight : 0) + (nb1C ? nb1C.offsetHeight : 0)) + 'px';
          // Bootstrap popover() removed — use native tooltips or data-bs-toggle if needed

          self.setGameScheduleNumbers(256);

          self.setExport();
          super.initTeamSubscribers();
          super.finishComputing();
        })
        .catch((err: unknown) => {
          console.error('NFL schedule-picker load error:', err);
          const loadingEl = document.getElementById('loading');
          if (loadingEl) loadingEl.textContent = 'An error occurred loading the schedule.';
        });
    }
    const bracketsEl = document.getElementById('brackets');
    if (bracketsEl) bracketsEl.style.display = 'none';
    document.addEventListener('click', (e) => {
      if ((e.target as Element).closest('#bracketUpload')) {
        const brackets = document.getElementById('brackets');
        if (!brackets) return;
        brackets.querySelectorAll('a').forEach(a => a.remove());
        const uploadingSpan = document.createElement('span');
        uploadingSpan.className = 'pull-right';
        uploadingSpan.textContent = 'Uploading...';
        brackets.appendChild(uploadingSpan);
        var canvas: any = document.querySelector('canvas');
        var dataUrl = canvas.toDataURL('image/png', 1.0);
        ppFetch<any>({
          url: '/Football/CustomLeague/UploadBracket',
          method: 'POST',
          data: {
            Base64: dataUrl,
            LeagueGuid: leagueGuid,
            SaveString:
              SchedulePickerBase.getQueryVariable('L') || '' || localStorage.getItem(leagueGuid),
          },
        }).then((ajaxResult) => {
          brackets.querySelectorAll('span').forEach(s => s.remove());
          const linkA = document.createElement('a');
          linkA.href = `${getWebAppBaseUrl()}/${ajaxResult.Model.link}`;
          linkA.className = 'pull-right';
          linkA.textContent = 'Bracket Link';
          brackets.appendChild(linkA);
          var tweetText =
            'I just picked who I think will win next year\'s super bowl on %23PlayoffPredictors ' + getWebAppBaseUrl() +
            ajaxResult.Model.link;
          brackets.insertAdjacentHTML('beforeend',
            `<a class="twitter-share-button" href="https://x.com/intent/tweet?text=${tweetText}">Tweet</a><a href="https://x.com/RayLehnhoff?ref_src=twsrc%5Etfw" class="twitter-follow-button" data-show-count="false">Follow @RayLehnhoff</a>`
          );
          window.twttr.widgets.load(brackets);
        });
      }
    });

    console.log(self);
  }

  setUrlRoute(isEmptyString?: boolean): void {
    var self = this;
    var route;
    const leagueGuid = self.config.leagueGuid;
    if (isEmptyString) {
      route = `https://${location.host}/Football/CustomLeague/SchedulePicker/${leagueGuid}/?L=${self.EmptySeasonString}`;
    } else {
      route = `https://${location.host}/Football/CustomLeague/SchedulePicker/${leagueGuid}/?L=${self.SaveString()}`;
    }

    route += `&c=cgm:${userConfigManager.UserConfig.NFL.CommonGamesMethodology}|tnptlw:${userConfigManager.UserConfig.NFL.TreatNonPlayoffTeamsLikeWildcards}`;

    self.UrlRoute(route);
  }

  randomize(): void {
    var self = this;
    let numGames = self.NumGames();

    if (
      confirm(
        'All of your regular season selections will be randomized, replacing your current selections. Are you sure you want to proceed?'
      )
    ) {
      let numGames = self.NumGames();
      for (let i = 0; i < numGames; i++) {
        let selection = 0;
        for (let j = 0; j < 4; j++) {
          selection = (Math.floor(Math.random() * 3) + 1) as SelectionType;
          if (selection !== SelectionType.TIE) {
            break;
          }
        }
        self.GameArray[i] = selection;
      }
      if (self.PostSeasonVersion == 1) {
        for (let i = numGames; i < 267; i++) {
          self.GameArray[i] = 0;
        }
      } else if (self.PostSeasonVersion == 2) {
        for (let i = numGames; i < 269; i++) {
          self.GameArray[i] = 0;
        }
      }

      self.updateSaveString();
      self.load(self.GameArray.join(''));

      for (let team of self.AllTeams()) {
        team().FakeObs.valueHasMutated();
        team().Wins.valueHasMutated();
        team().Losses.valueHasMutated();
        team().Ties.valueHasMutated();
      }
      self.UpdateStats(-1, true);
      self.SelectedWeek({ key: self.MaxWeek(), value: self.Weeks[self.MaxWeek()] });
    }
  }

  randomizeWeek(): void {
    var self = this;
    for (let week of self.SelectedWeek().value) {
      for (let gameObs of week.games()) {
        let game = gameObs() as NflGame;
        self.unselectGame(gameObs, true);
        let selection = 0;
        if (self.SelectedWeek().key <= self.MaxWeek()) {
          for (let j = 0; j < 4; j++) {
            selection = (Math.floor(Math.random() * 3) + 1) as SelectionType;
            if (selection !== SelectionType.TIE) {
              break;
            }
          }
        } else {
          selection = Math.floor(Math.random() * 2) + 1;
        }
        var teamSelected;
        switch (selection) {
          case SelectionType.AWAY_WIN:
            teamSelected = game.away;
            break;
          case SelectionType.HOME_WIN:
            teamSelected = game.home;
            break;
          case SelectionType.TIE:
            teamSelected = 'TIE';
            break;
          default:
            teamSelected = game.home;
            break;
        }
        self.selectGame(gameObs, teamSelected, selection, true);
        if (selection != SelectionType.TIE) {
          game.TeamThatWon()().GameRecords.valueHasMutated();
          game.TeamThatLost()().GameRecords.valueHasMutated();
        }
      }
    }
    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }

  resetWeek(): void {
    const self = this;
    for (let week of self.SelectedWeek().value) {
      for (let gameObs of week.games()) {
        let game = gameObs() as NflGame;
        if (game.selectionMade()) {
          if (game.selectionType() !== SelectionType.TIE) {
            self.selectGame(gameObs, game.TeamThatWon(), SelectionType.NONE, true);
          } else {
            self.selectGame(gameObs, 'TIE', SelectionType.NONE, true);
          }
        }
      }
    }

    self.UpdateStats(-1, self.SelectedWeek().key <= self.MaxWeek());
  }

  convertWeekKeyToName(keyStr: string): string {
    var self = this;
    var maxWeek = self.MaxWeek();
    var key = parseInt(keyStr, 10);
    if (key <= maxWeek) {
      return key.toString();
    } else if (key === maxWeek + 1) {
      return 'WC';
    } else if (key === maxWeek + 2) {
      return 'DV';
    } else if (key === maxWeek + 3) {
      return 'CG';
    } else {
      return 'SB';
    }
  }

  getTeamsOnByeForWeek(weekNum: number): KnockoutObservable<NflTeam>[] {
    var self = this;
    var maxWeek = self.MaxWeek();

    if (weekNum == maxWeek + 1) {
      if (self.PostSeasonVersion == 2) {
        return [self.AFCTop()[0], self.NFCTop()[0]];
      }
      return [self.AFCTop()[0], self.AFCTop()[1], self.NFCTop()[0], self.NFCTop()[1]];
    }
    if (weekNum > self.MaxWeek() || weekNum < 1) return [];
    var teamsNotSeen = [];
    for (var i = 1; i <= self.NumberOfTeams(); i++) {
      teamsNotSeen.push(i);
    }
    var id;
    for (id of self.Weeks[weekNum][0].TeamsInWeek()) {
      if (teamsNotSeen.indexOf(id) != -1) {
        teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
      }
    }
    for (id of self.Weeks[weekNum][1].TeamsInWeek()) {
      if (teamsNotSeen.indexOf(id) != -1) {
        teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
      }
    }
    for (id of self.Weeks[weekNum][2].TeamsInWeek()) {
      if (teamsNotSeen.indexOf(id) != -1) {
        teamsNotSeen.splice(teamsNotSeen.indexOf(id), 1);
      }
    }
    var distinctTeams = ko.utils.arrayGetDistinctValues(teamsNotSeen);
    return Enumerable.From(
      distinctTeams.map((id) => {
        return self.findTeamByID(id);
      })
    )
      .OrderBy((p: KnockoutObservable<NflTeam>) => p().TeamNameObs())
      .ToArray();
  }

  wasTeamOnByeLastWeek(teamObs: KnockoutObservable<NflTeam>, weekNum?: number): boolean {
    var self = this;
    if (weekNum == null) {
      weekNum = +self.SelectedWeek().key;
    }
    var teamsOnBye = self.getTeamsOnByeForWeek(weekNum - 1);
    if (teamsOnBye.length == 0) return false;
    return teamsOnBye.indexOf(teamObs) != -1;
  }

  setExport(): void {
    var self = this;
    var htmlOutput = (() => {
      var html = '<div>';
      html += '<h3>AFC</h3>';
      var confHtml = '<div>';
      var conf: string, team: any, style: any;
      for (let div of mapDictionaryToArray(self.Teams[0].value)) {
        conf = `<table>
                <thead><tr><th>#</th><th>AFC ${div.key}</th> <th>WLT < /th><th>Div</th > </tr></thead>
                    <tbody>`;
        for (let teamRef of mapDictionaryToArray(div.value).sort(rankSort)) {
          team = teamRef.value();
          style =
            team.ConferenceCss() == 'in-post-season'
              ? 'color:green; font-weight:bold'
              : 'color:red;font-style:italic;';
          conf += `<tr> <td style="${style}" > ${team.ConferenceRankText()} </td><td><a href='${self.SaveVal() + '#' + team.TeamAbbreviation}'>${team.TeamNameObs()}</a> </td><td>${team.GameRecords.AllRecord.toString()}</td> <td>${team.GameRecords.DivisionRecord.toString()} </td></tr> `;
        }
        conf += `</table>`;
        confHtml += conf;
      }
      confHtml += '</div>';
      html += confHtml;
      confHtml = '<div>';
      confHtml += '<h3>NFC</h3>';
      for (let div of mapDictionaryToArray(self.Teams[1].value)) {
        conf = `<table>
                            <thead><tr><th>#</th><th>NFC ${div.key}</th><th>WLT</th><th>Div</th></tr></thead>
                            <tbody>`;
        for (let teamRef of mapDictionaryToArray(div.value).sort(rankSort)) {
          team = teamRef.value();
          style =
            team.ConferenceCss() == 'in-post-season'
              ? 'color:green; font-weight:bold'
              : 'color:red;font-style:italic;';
          conf += `<tr><td style="${style}">${team.ConferenceRankText()}</td><td><a href='${self.SaveVal() + '#' + team.TeamAbbreviation}'>${team.TeamNameObs()}</a></td><td>${team.GameRecords.AllRecord.toString()}</td><td>${team.GameRecords.DivisionRecord.toString()}</td></tr>`;
        }
        conf += `</table>`;
        confHtml += conf;
      }
      confHtml += '</div>';
      html += confHtml;
      html += `<a href='${self.SaveVal()}'>Generated By The NFL Playoff Predictor</a>`;
      html += '</div>';
      return html;
    })();
    self.HtmlExport(htmlOutput);

    var $redditExport = (() => {
      var exportArray = [];
      var conferenceName = 'NFC';
      if (this.RNflStyles()) {
        conferenceName = '[](/NFC)NFC';
      }
      exportArray.push('#' + conferenceName + '\n\nRank | Team | W | L | T\n-|-|-:|-:|-:|\n');
      ko.utils.arrayForEach(this.NFCTop(), (item: KnockoutObservable<NflTeam>, idx: number) => {
        var teamName =
          '[' +
          item().TeamAbbreviation +
          '](' +
          this.SaveVal() +
          '#' +
          item().TeamAbbreviation +
          ')';
        if (this.RNflStyles()) {
          teamName = '[](' + item().TeamAbbreviation + ')' + teamName;
        }
        exportArray.push(
          idx + 1,
          '|',
          teamName,
          '|',
          item().GameRecords.AllRecord.Wins(),
          '|',
          item().GameRecords.AllRecord.Losses(),
          '|',
          item().GameRecords.AllRecord.Ties(),
          '\n'
        );
      });
      exportArray.push('\n\n');
      conferenceName = 'AFC';
      if (this.RNflStyles()) {
        conferenceName = '[](/AFC)AFC';
      }
      exportArray.push('#' + conferenceName + '\n\nRank | Team | W | L | T\n-|-|-:|-:|-:|\n');
      ko.utils.arrayForEach(this.AFCTop(), (item: KnockoutObservable<NflTeam>, idx: number) => {
        var teamName =
          '[' +
          item().TeamAbbreviation +
          '](' +
          this.SaveVal() +
          '#' +
          item().TeamAbbreviation +
          ')';
        if (this.RNflStyles()) {
          teamName = '[](' + item().TeamAbbreviation + ')' + teamName;
        }
        exportArray.push(
          idx + 1,
          '|',
          teamName,
          '|',
          item().GameRecords.AllRecord.Wins(),
          '|',
          item().GameRecords.AllRecord.Losses(),
          '|',
          item().GameRecords.AllRecord.Ties(),
          '\n'
        );
      });
      exportArray.push('\n\nGenerated by The [NFL Playoff Predictor](', this.SaveVal(), ')');
      return exportArray.join('');
    })();

    self.Export($redditExport);
  }

  previewHtml(): void {
    var win = window.open(
      '',
      'Title',
      'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=yes, resizable=yes, width=780, height=200, top=' +
        (screen.height - 400) +
        ', left=' +
        (screen.width - 840)
    );
    win.document.body.innerHTML = this.HtmlExport();
  }

  init(replaceTeams?: any[]): void {
    var self = this;
    var allTeams = [],
      afcDivisions = this.Dict['AFC'],
      divisionInd: string,
      division: any,
      teamInd: string,
      team: any;

    for (divisionInd in afcDivisions) {
      division = afcDivisions[divisionInd];
      for (teamInd in division) {
        team = division[teamInd];
        allTeams.push(team);
      }
    }

    var nfcDivisions = this.Dict['NFC'];
    for (divisionInd in nfcDivisions) {
      division = nfcDivisions[divisionInd];
      for (teamInd in division) {
        team = division[teamInd];
        allTeams.push(team);
      }
    }
    this.AllTeams(allTeams);

    if (replaceTeams) {
      for (var teamOverride of replaceTeams) {
        var teamReplaced: any = Enumerable.From(this.AllTeams()).First(
          (p: any) => p().TeamID === teamOverride.TeamReplaced
        );
        this.AllTeams.remove(teamReplaced);
        teamReplaced().RegionName = teamOverride.RegionName;
        teamReplaced().TeamNameObs(teamOverride.TeamName);
        teamReplaced().TeamName = teamOverride.TeamName;
        teamReplaced().Conference = teamOverride.Conference;
        teamReplaced().Division = teamOverride.Division;
        this.AllTeams.push(teamReplaced);
      }
    }

    this.SelectedTeam.subscribe((newVal) => {
      if (newVal) {
        this.IsTeamSelected(true);
        // bootstrapSwitch removed; KO checked binding handles toggle state
      }
    });
  }

  unselectGame(gameInput: KnockoutObservable<IGame>, skipUpdate?: boolean): void {
    var self = this;
    gameInput().selectionMade(false);
    gameInput().teamSelected('');
    self.GameArray[gameInput().gameScheduleNum()] = 0;
    self.updateSaveString();
    if (!skipUpdate) {
      self.UpdateStats(-1, true);
    }
  }

  gameMap: Record<string | number, { position: number; result: number } | number> = {};

  load(urlString: string): void {
    if (this.Version == 2) {
      this.loadV2(urlString);
    } else {
      this.loadV1(urlString);
    }
  }

  loadV2(urlString: string): void {
    var self = this;
    if (urlString.length == self.NumGames()) {
      urlString += '0000000000000';
    }

    self.BlockHistoryPush(true);
    var biggestWeek = 1;
    if (self.IsAfterFirstLoad) {
      for (let weekInd in self.Weeks) {
        const gameType = self.Weeks[weekInd];
        ko.utils.arrayForEach(gameType, (week: any) => {
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<any>) => {
            game().selectionType(SelectionType.NONE);
            game().selectionMade(false);
            game().teamSelected('');
          });
        });
      }
      const bracketsContainer = document.getElementById('brackets');
      if (bracketsContainer) bracketsContainer.innerHTML = '';

      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]();
        team.reset();
        team.FakeObs();
      }
    }
    self.IsAfterFirstLoad = true;
    let numGames = self.NumGames();
    for (let i = 0; i < urlString.length; i++) {
      var selection: SelectionType = parseInt(urlString[i], 10);

      var skipUpdate = i <= numGames - 1;
      if (i == numGames) {
        self.initTeamSubscribers();
        self.UpdateStats(i, true);
      }
      if (i > numGames) {
        //We'll need to dynamically create these games
        if (self.PostSeasonVersion == 1) {
          if (i <= numGames + 4) {
            self.seedDV();
          } else if (i <= numGames + 4 + 4) {
            self.seedCG();
          } else if (i <= numGames + 4 + 4 + 2) {
            self.seedSB();
          }
        } else if (self.PostSeasonVersion == 2) {
          if (i <= numGames + 6) {
            self.seedDV();
          } else if (i <= numGames + 6 + 4) {
            self.seedCG();
          } else if (i <= numGames + 6 + 4 + 2) {
            self.seedSB();
          }
        }
      }
      let game = self.GameScheduleGameMap[i];
      if (!game) continue;
      let _game = game();
      let home = _game.home();
      let away = _game.away();
      let weekInd = _game.week;

      let input = {
        gameScheduleNum: _game.gameScheduleNum(),
        home: {
          Conference: home.Conference,
          Division: home.Division,
          TeamName: home.TeamNameObs(),
          Pct: home.GameRecords.AllRecord.Pct,
        },
        away: {
          Conference: away.Conference,
          Division: away.Division,
          TeamName: away.TeamNameObs(),
          Pct: away.GameRecords.AllRecord.Pct,
        },
      } as unknown as IGame;
      switch (selection) {
        case SelectionType.AWAY_WIN:
          self.GameArray[i] = SelectionType.AWAY_WIN;
          if (biggestWeek < +weekInd) {
            biggestWeek = +weekInd;
          }
          if (game().gameScheduleNum() <= numGames - 1) {
            away.Wins.push(input);
            home.Losses.push(input);
          }
          self.gameSelected(game, game().away, selection, i, skipUpdate);
          break;
        case SelectionType.HOME_WIN:
          self.GameArray[i] = SelectionType.HOME_WIN;
          if (biggestWeek < +weekInd) {
            biggestWeek = +weekInd;
          }
          if (game().gameScheduleNum() <= numGames - 1) {
            away.Losses.push(input);
            home.Wins.push(input);
          }
          self.gameSelected(game, game().home, selection, i, skipUpdate);
          break;
        case SelectionType.NONE:
          if (game().selectionType() !== SelectionType.NONE) {
            self.unselectGame(game, skipUpdate);
          }
          self.GameArray[i] = SelectionType.NONE;
          break;
        case SelectionType.TIE:
          self.GameArray[i] = SelectionType.TIE;
          if (biggestWeek < +weekInd) {
            biggestWeek = +weekInd;
          }
          if (game().gameScheduleNum() <= numGames - 1) {
            away.Ties.push(input);
            home.Ties.push(input);
          }
          self.gameSelected(game, 'TIE', selection, i, skipUpdate);
          break;
      }
      this.gameMap[game().gameKey] = { position: i, result: 0 };
    }
    var week = NflApp.getQueryVariable('week');
    if (week) {
      var weekParsed = parseInt(week, 10);
      self.switchContext({ key: weekParsed, value: self.Weeks[weekParsed] });
      self.BiggestWeek(weekParsed);
    } else {
      self.switchContext({ key: biggestWeek, value: self.Weeks[biggestWeek] });
      self.BiggestWeek(biggestWeek);
    }
    if (self.Hash()) {
      try {
        var team = self.findTeam(self.Hash().slice(1))();
        self.showTeamStats(team);
        self.IsTeamSelected(true);
      } catch (ex) {
        console.warn(ex);
      }
    }

    self.BlockHistoryPush(true);
    self.updateSaveString();
    self.pushHistory();
    self.BlockHistoryPush(false);
  }

  loadV1(urlString: string): void {
    var self = this;
    var position = 0;
    var offset = 0;
    self.BlockHistoryPush(true);
    let numGames = self.NumGames();
    var biggestWeek = 1;
    if (self.IsAfterFirstLoad) {
      for (let weekInd in self.Weeks) {
        const gameType = self.Weeks[weekInd];
        ko.utils.arrayForEach(gameType, (week: any) => {
          ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<any>) => {
            game().selectionType(SelectionType.NONE);
            game().selectionMade(false);
            game().teamSelected('');
          });
        });
      }
      const bracketsContainer = document.getElementById('brackets');
      if (bracketsContainer) bracketsContainer.innerHTML = '';

      for (let i = 0; i < self.AllTeams().length; i++) {
        const team = self.AllTeams()[i]();
        team.reset();
        team.FakeObs();
      }
    }
    self.IsAfterFirstLoad = true;
    for (var weekInd in self.Weeks) {
      var gameType = self.Weeks[weekInd];
      ko.utils.arrayForEach(gameType, (week: NflWeek) => {
        if (position == numGames) {
          self.initTeamSubscribers();
          self.UpdateStats(position, true);
        }
        if (position > numGames) {
          if (self.PostSeasonVersion == 1) {
            if (position <= numGames + 4) {
              self.seedDV();
            } else if (position <= numGames + 4 + 4) {
              self.seedCG();
            } else if (position <= numGames + 4 + 4 + 2) {
              self.seedSB();
            }
          } else if (self.PostSeasonVersion == 2) {
            if (position <= numGames + 6) {
              self.seedDV();
            } else if (position <= numGames + 6 + 4) {
              self.seedCG();
            } else if (position <= numGames + 6 + 4 + 2) {
              self.seedSB();
            }
          }
        }
        ko.utils.arrayForEach(week.GamesComputed(), (game: KnockoutObservable<IGame>) => {
          let _game = game();
          let home = _game.home();
          let away = _game.away();

          var skipUpdate = position <= numGames - 1;
          if (urlString.length > numGames) {
            skipUpdate = true;
          }
          if (position === numGames - 1) {
            skipUpdate = false;
          }
          var selection: SelectionType = parseInt(urlString[position], 10);

          if (game() && !game().gameScheduleNum()) {
            if (position - offset < numGames) {
              game().gameScheduleNum(position - offset);
            }
          } else {
            offset++;
          }
          let input = {
            gameScheduleNum: _game.gameScheduleNum(),
            home: {
              Conference: home.Conference,
              Division: home.Division,
              TeamName: home.TeamNameObs(),
              Pct: home.GameRecords.AllRecord.Pct,
            },
            away: {
              Conference: away.Conference,
              Division: away.Division,
              TeamName: away.TeamNameObs(),
              Pct: away.GameRecords.AllRecord.Pct,
            },
          } as unknown as IGame;
          switch (selection) {
            case SelectionType.AWAY_WIN:
              self.GameArray[position] = SelectionType.AWAY_WIN;
              if (biggestWeek < +weekInd) {
                biggestWeek = +weekInd;
              }
              if (game().gameScheduleNum() <= numGames - 1) {
                away.Wins.push(input);
                home.Losses.push(input);
              }
              self.gameSelected(game, game().away, selection, position, skipUpdate);
              break;
            case SelectionType.HOME_WIN:
              self.GameArray[position] = SelectionType.HOME_WIN;
              if (biggestWeek < +weekInd) {
                biggestWeek = +weekInd;
              }
              if (game().gameScheduleNum() <= numGames - 1) {
                away.Losses.push(input);
                home.Wins.push(input);
              }
              self.gameSelected(game, game().home, selection, position, skipUpdate);
              break;
            case SelectionType.NONE:
              self.GameArray[position] = SelectionType.NONE;
              self.unselectGame(game, skipUpdate);
              break;
            case SelectionType.TIE:
              self.GameArray[position] = SelectionType.TIE;
              if (biggestWeek < +weekInd) {
                biggestWeek = +weekInd;
              }
              if (game().gameScheduleNum() <= numGames - 1) {
                away.Ties.push(input);
                home.Ties.push(input);
              }
              self.gameSelected(game, 'TIE', selection, position, skipUpdate);
              break;
          }
          this.gameMap[game().gameKey] = { position: position, result: 0 };
          position++;
        });
      });
    }
    var week = NflApp.getQueryVariable('week');
    if (week) {
      var weekParsed = parseInt(week, 10);
      self.switchContext({ key: weekParsed, value: self.Weeks[weekParsed] });
      self.BiggestWeek(weekParsed);
    } else {
      self.switchContext({ key: biggestWeek, value: self.Weeks[biggestWeek] });
      self.BiggestWeek(biggestWeek);
    }
    if (self.Hash()) {
      try {
        var team = self.findTeam(self.Hash().slice(1))();
        self.showTeamStats(team);
        self.IsTeamSelected(true);
      } catch (ex) {
        console.warn(ex);
      }
    }
    self.UpdateStats(position, false);

    self.pushHistory();
    self.BlockHistoryPush(false);
  }

  pushHistory(): void {
    let self = this;
    const maxWeek = self.MaxWeek();
    self.generateDraftOrder(
      self.Weeks[maxWeek + 1],
      self.Weeks[maxWeek + 2],
      self.Weeks[maxWeek + 3],
      self.Weeks[maxWeek + 4]
    );
    BracketGenerator.GenerateBracket(
      self.Weeks[maxWeek + 1],
      self.Weeks[maxWeek + 2],
      self.Weeks[maxWeek + 3],
      self.Weeks[maxWeek + 4],
      self.PostSeasonVersion
    );
    super.pushHistory();
  }

  Timeout: ReturnType<typeof setTimeout> | undefined;

  seedDV(): void {
    var first: KnockoutObservable<NflTeam>;
    var second: KnockoutObservable<NflTeam>;
    let numGames = this.NumGames();
    let wcWeekStart = this.MaxWeek() + 1;
    var gameIds: number[] = [];
    var dvTeams: any[];
    if (this.PostSeasonVersion == 1) {
      gameIds = [numGames + 4, numGames + 5, numGames + 6, numGames + 7];
      if (
        this.Weeks[wcWeekStart][0].GamesComputed()[0]().selectionMade() &&
        this.Weeks[wcWeekStart][0].GamesComputed()[1]().selectionMade()
      ) {
        first = this.findTeam(
          this.Weeks[wcWeekStart][0].GamesComputed()[0]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        second = this.findTeam(
          this.Weeks[wcWeekStart][0].GamesComputed()[1]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        dvTeams = [first, second, this.AFCTop()[0], this.AFCTop()[1]];
        dvTeams = Enumerable.From(dvTeams)
          .OrderBy((p: KnockoutObservable<NflTeam>) => p().ConferenceRank())
          .ToArray();
        if (
          this.Weeks[wcWeekStart + 1][0].GamesComputed().length === 0 ||
          this.Weeks[wcWeekStart + 1][0].GamesComputed()[0]().selectionMade() ||
          this.Weeks[wcWeekStart + 1][0].GamesComputed()[1]().selectionMade()
        ) {
          var dv1 = ko.observable(
            new NflGame(this.AFCTop()[1], dvTeams[2], wcWeekStart + 1)
          );
          dv1().gameScheduleNum(gameIds[0]);
          this.GameScheduleGameMap[gameIds[0]] = dv1;
          this.GameArray[gameIds[0]] = 0;
          var dv2 = ko.observable(
            new NflGame(this.AFCTop()[0], dvTeams[3], wcWeekStart + 1)
          );
          dv2().gameScheduleNum(gameIds[1]);
          this.GameScheduleGameMap[gameIds[1]] = dv2;
          this.GameArray[gameIds[1]] = 0;
          this.Weeks[wcWeekStart + 1][0].games([dv1, dv2]);
        }
      }
    } else if (this.PostSeasonVersion == 2) {
      gameIds = [numGames + 6, numGames + 7, numGames + 8, numGames + 9];
      if (
        this.Weeks[wcWeekStart][0].GamesComputed()[0]().selectionMade() &&
        this.Weeks[wcWeekStart][0].GamesComputed()[1]().selectionMade() &&
        this.Weeks[wcWeekStart][0].GamesComputed()[2]().selectionMade()
      ) {
        first = this.findTeam(
          this.Weeks[wcWeekStart][0].GamesComputed()[0]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        second = this.findTeam(
          this.Weeks[wcWeekStart][0].GamesComputed()[1]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        let third = this.findTeam(
          this.Weeks[wcWeekStart][0].GamesComputed()[2]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        dvTeams = [first, second, this.AFCTop()[0], third];
        dvTeams = Enumerable.From(dvTeams)
          .OrderBy((p: KnockoutObservable<NflTeam>) => p().ConferenceRank())
          .ToArray();
        if (
          this.Weeks[wcWeekStart + 1][0].GamesComputed().length === 0 ||
          this.Weeks[wcWeekStart + 1][0].GamesComputed()[0]().selectionMade() ||
          this.Weeks[wcWeekStart + 1][0].GamesComputed()[1]().selectionMade()
        ) {
          let dv1 = ko.observable(new NflGame(dvTeams[1], dvTeams[2], wcWeekStart + 1));
          dv1().gameScheduleNum(gameIds[0]);
          this.GameScheduleGameMap[gameIds[0]] = dv1;
          this.GameArray[gameIds[0]] = 0;
          let dv2 = ko.observable(
            new NflGame(this.AFCTop()[0], dvTeams[3], wcWeekStart + 1)
          );
          dv2().gameScheduleNum(gameIds[1]);
          this.GameScheduleGameMap[gameIds[1]] = dv2;
          this.GameArray[gameIds[1]] = 0;
          this.Weeks[wcWeekStart + 1][0].games([dv1, dv2]);
        }
      }
    }

    if (this.PostSeasonVersion == 1) {
      if (
        this.Weeks[wcWeekStart][2].GamesComputed()[0]().selectionMade() &&
        this.Weeks[wcWeekStart][2].GamesComputed()[1]().selectionMade()
      ) {
        first = this.findTeam(
          this.Weeks[wcWeekStart][2].GamesComputed()[0]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        second = this.findTeam(
          this.Weeks[wcWeekStart][2].GamesComputed()[1]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        dvTeams = [first, second, this.NFCTop()[0], this.NFCTop()[1]];
        dvTeams = Enumerable.From(dvTeams)
          .OrderBy((p: any) => p().ConferenceRank())
          .ToArray();
        if (this.Weeks[wcWeekStart + 1]) {
          if (
            this.Weeks[wcWeekStart + 1][2].GamesComputed().length === 0 ||
            this.Weeks[wcWeekStart + 1][2].GamesComputed()[0]().selectionMade() ||
            this.Weeks[wcWeekStart + 1][2].GamesComputed()[1]().selectionMade()
          ) {
            let dv3 = ko.observable(
              new NflGame(this.NFCTop()[1], dvTeams[2], wcWeekStart + 1)
            );
            dv3().gameScheduleNum(gameIds[2]);
            this.GameScheduleGameMap[gameIds[2]] = dv3;
            this.GameArray[gameIds[2]] = 0;
            let dv4 = ko.observable(
              new NflGame(this.NFCTop()[0], dvTeams[3], wcWeekStart + 1)
            );
            dv4().gameScheduleNum(gameIds[3]);
            this.GameScheduleGameMap[gameIds[3]] = dv4;
            this.GameArray[gameIds[3]] = 0;
            this.Weeks[wcWeekStart + 1][2].games([dv3, dv4]);
          }
        }
      }
    } else if (this.PostSeasonVersion == 2) {
      if (
        this.Weeks[wcWeekStart][2].GamesComputed()[0]().selectionMade() &&
        this.Weeks[wcWeekStart][2].GamesComputed()[1]().selectionMade() &&
        this.Weeks[wcWeekStart][2].GamesComputed()[2]().selectionMade()
      ) {
        first = this.findTeam(
          this.Weeks[wcWeekStart][2].GamesComputed()[0]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        second = this.findTeam(
          this.Weeks[wcWeekStart][2].GamesComputed()[1]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        let third = this.findTeam(
          this.Weeks[wcWeekStart][2].GamesComputed()[2]().TeamThatWon()().TeamAbbreviation
        ) as KnockoutObservable<NflTeam>;
        dvTeams = [first, second, this.NFCTop()[0], third];
        dvTeams = Enumerable.From(dvTeams)
          .OrderBy((p: any) => p().ConferenceRank())
          .ToArray();
        if (this.Weeks[wcWeekStart + 1]) {
          if (
            this.Weeks[wcWeekStart + 1][2].GamesComputed().length === 0 ||
            this.Weeks[wcWeekStart + 1][2].GamesComputed()[0]().selectionMade() ||
            this.Weeks[wcWeekStart + 1][2].GamesComputed()[1]().selectionMade()
          ) {
            let dv3 = ko.observable(new NflGame(dvTeams[1], dvTeams[2], wcWeekStart + 1));
            dv3().gameScheduleNum(gameIds[2]);
            this.GameScheduleGameMap[gameIds[2]] = dv3;
            this.GameArray[gameIds[2]] = 0;
            let dv4 = ko.observable(
              new NflGame(this.NFCTop()[0], dvTeams[3], wcWeekStart + 1)
            );
            dv4().gameScheduleNum(gameIds[3]);
            this.GameScheduleGameMap[gameIds[3]] = dv4;
            this.GameArray[gameIds[3]] = 0;
            this.Weeks[wcWeekStart + 1][2].games([dv3, dv4]);
          }
        }
      }
    }
  }

  seedCG(): void {
    var firstGame: NflTeam;
    var secondGame: NflTeam;
    let numGames = this.NumGames();
    let dvWeekStart = this.MaxWeek() + 2;
    var gameIds: number[] = [];
    if (this.PostSeasonVersion == 1) {
      gameIds = [numGames + 8, numGames + 9];
    } else if (this.PostSeasonVersion == 2) {
      gameIds = [numGames + 10, numGames + 11];
    }
    if (this.Weeks[dvWeekStart][0].GamesComputed().length === 2) {
      if (
        this.Weeks[dvWeekStart][0].GamesComputed()[0]().selectionMade() &&
        this.Weeks[dvWeekStart][0].GamesComputed()[1]().selectionMade()
      ) {
        firstGame = this.Weeks[dvWeekStart][0].GamesComputed()[0]().TeamThatWon()() as NflTeam;
        secondGame = this.Weeks[dvWeekStart][0].GamesComputed()[1]().TeamThatWon()() as NflTeam;
        if (firstGame.ConferenceRank() > secondGame.ConferenceRank()) {
          var cg1 = ko.observable(
            new NflGame(
              this.findTeam(secondGame.TeamAbbreviation),
              this.findTeam(firstGame.TeamAbbreviation),
              dvWeekStart + 1
            )
          );
          cg1().gameScheduleNum(gameIds[0]);
          this.GameScheduleGameMap[gameIds[0]] = cg1;
          this.Weeks[dvWeekStart + 1][0].games([cg1]);
        } else {
          var cg2 = ko.observable(
            new NflGame(
              this.findTeam(firstGame.TeamAbbreviation),
              this.findTeam(secondGame.TeamAbbreviation),
              dvWeekStart + 1
            )
          );
          cg2().gameScheduleNum(gameIds[0]);
          this.GameScheduleGameMap[gameIds[0]] = cg2;
          this.Weeks[dvWeekStart + 1][0].games([cg2]);
        }
        this.GameArray[gameIds[0]] = 0;
      }
    }

    if (this.Weeks[dvWeekStart][2].GamesComputed().length === 2) {
      if (
        this.Weeks[dvWeekStart][2].GamesComputed()[0]().selectionMade() &&
        this.Weeks[dvWeekStart][2].GamesComputed()[1]().selectionMade()
      ) {
        firstGame = this.Weeks[dvWeekStart][2].GamesComputed()[0]().TeamThatWon()() as NflTeam;
        secondGame = this.Weeks[dvWeekStart][2].GamesComputed()[1]().TeamThatWon()() as NflTeam;
        if (firstGame.ConferenceRank() > secondGame.ConferenceRank()) {
          let cg3 = ko.observable(
            new NflGame(
              this.findTeam(secondGame.TeamAbbreviation),
              this.findTeam(firstGame.TeamAbbreviation),
              dvWeekStart + 1
            )
          );
          cg3().gameScheduleNum(gameIds[1]);
          this.GameScheduleGameMap[gameIds[1]] = cg3;
          this.Weeks[dvWeekStart + 1][2].games([cg3]);
        } else {
          let cg3 = ko.observable(
            new NflGame(
              this.findTeam(firstGame.TeamAbbreviation),
              this.findTeam(secondGame.TeamAbbreviation),
              dvWeekStart + 1
            )
          );
          cg3().gameScheduleNum(gameIds[1]);
          this.GameScheduleGameMap[gameIds[1]] = cg3;
          this.Weeks[dvWeekStart + 1][2].games([cg3]);
        }
        this.GameArray[gameIds[1]] = 0;
      }
    }
  }

  seedSB(): void {
    var self = this;
    let numGames = this.NumGames();
    let cgWeekStart = this.MaxWeek() + 3;
    var gameIds: number[] = [];
    if (this.PostSeasonVersion == 1) {
      gameIds = [numGames + 10];
    } else if (this.PostSeasonVersion == 2) {
      gameIds = [numGames + 12];
    }
    if (
      this.Weeks[cgWeekStart][0].GamesComputed().length !== 1 ||
      this.Weeks[cgWeekStart][2].GamesComputed().length !== 1
    ) {
      return;
    }
    if (
      this.Weeks[cgWeekStart][0].GamesComputed()[0]().selectionMade() &&
      this.Weeks[cgWeekStart][2].GamesComputed()[0]().selectionMade()
    ) {
      var afcChamp = this.findTeam(
        this.Weeks[cgWeekStart][0].GamesComputed()[0]().TeamThatWon()().TeamAbbreviation
      );
      var nfcChamp = this.findTeam(
        this.Weeks[cgWeekStart][2].GamesComputed()[0]().TeamThatWon()().TeamAbbreviation
      );

      var afcHome = self.SeasonYearEnd % 2 === 0;
      if (afcHome) {
        let sb = ko.observable(new NflGame(afcChamp, nfcChamp, self.TotalWeeks));
        sb().gameScheduleNum(gameIds[0]);
        this.GameScheduleGameMap[gameIds[0]] = sb;
        this.Weeks[self.TotalWeeks][1].games([sb]);
      } else {
        let sb = ko.observable(new NflGame(nfcChamp, afcChamp, self.TotalWeeks));
        sb().gameScheduleNum(gameIds[0]);
        this.GameScheduleGameMap[gameIds[0]] = sb;
        this.Weeks[self.TotalWeeks][1].games([sb]);
      }
      this.GameArray[gameIds[0]] = 0;
      this.Weeks[self.TotalWeeks][1].games.valueHasMutated();
    }
  }

  selectGame(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    isRandomize: boolean
  ): void {
    var self = this;
    var _gameInput = gameInput(),
      home = _gameInput.home(),
      away = _gameInput.away(),
      input = {
        gameScheduleNum: _gameInput.gameScheduleNum(),
        home: {
          Conference: home.Conference,
          Division: home.Division,
          TeamName: home.TeamNameObs(),
          Pct: home.GameRecords.AllRecord.Pct,
        },
        away: {
          Conference: away.Conference,
          Division: away.Division,
          TeamName: away.TeamNameObs(),
          Pct: away.GameRecords.AllRecord.Pct,
        },
      } as unknown as IGame;
    let maxWeek = self.MaxWeek();
    let numGames = self.NumGames();
    if (
      (typeof selectedTeam == 'function' &&
        selectedTeam().TeamAbbreviation === _gameInput.teamSelected()) ||
      (_gameInput.selectionType() === SelectionType.TIE && selectedTeam === 'TIE')
    ) {
      _gameInput.selectionMade(false);
      _gameInput.teamSelected('');
      _gameInput.selectionType(SelectionType.NONE);
      self.GameArray[_gameInput.gameScheduleNum()] = SelectionType.NONE;
      home.Wins.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);
      away.Wins.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);

      home.Losses.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);
      away.Losses.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);

      home.Ties.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);
      away.Ties.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);

      home.Wins.valueHasMutated();
      home.Losses.valueHasMutated();
      home.Ties.valueHasMutated();
      away.Wins.valueHasMutated();
      away.Losses.valueHasMutated();
      away.Ties.valueHasMutated();
      clearTimeout(self.Timeout);
      self.Timeout = setTimeout(() => {
        self.updateSaveString();
        self.setSOSandSOV();
        self.UpdateRankings(Conferences.AFC, _gameInput.week < maxWeek + 1, -1);
        self.UpdateRankings(Conferences.NFC, _gameInput.week < maxWeek + 1, -1);
        self.pushHistory();
        super.finishComputing();
      }, 500);
    } else {
      self.GameArray[_gameInput.gameScheduleNum()] = selectionType;
      if (
        _gameInput.selectionMade() &&
        _gameInput.selectionType() === SelectionType.TIE
      ) {
        _gameInput.selectionMade(false);
        _gameInput.selectionType(SelectionType.NONE);
        _gameInput.teamSelected('');
        home.Ties.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);
        away.Ties.remove((p) => p.gameScheduleNum == (input as any).gameScheduleNum);
        home.Ties.valueHasMutated();
        away.Ties.valueHasMutated();
      }

      _gameInput.selectionMade(selectionType !== SelectionType.NONE);
      _gameInput.selectionType(selectionType);

      switch (selectionType) {
        case SelectionType.AWAY_WIN:
          _gameInput.teamSelected(away.TeamAbbreviation);
          break;
        case SelectionType.HOME_WIN:
          _gameInput.teamSelected(home.TeamAbbreviation);
          break;
        case SelectionType.NONE:
          _gameInput.teamSelected('');
          break;
        case SelectionType.TIE:
          _gameInput.teamSelected('TIE');
          break;
      }
      // This prevents records from updating if we're in the post season
      if (_gameInput.week < maxWeek + 1) {
        switch (selectionType) {
          case SelectionType.AWAY_WIN:
            _gameInput.teamSelected(away.TeamAbbreviation);
            away.Wins.push(input);
            home.Losses.push(input);
            home.Losses.valueHasMutated();
            away.Wins.valueHasMutated();
            break;
          case SelectionType.HOME_WIN:
            _gameInput.teamSelected(home.TeamAbbreviation);
            home.Wins.push(input);
            away.Losses.push(input);
            away.Losses.valueHasMutated();
            home.Wins.valueHasMutated();
            break;
          case SelectionType.NONE:
            _gameInput.teamSelected('');
            break;
          case SelectionType.TIE:
            _gameInput.teamSelected('TIE');
            home.Ties.push(input);
            away.Ties.push(input);
            break;
        }
      }
    }
    var selectionText: string;
    var selectedRank: any;
    var otherTeam: string;
    var opponentRank: any;
    switch (selectionType) {
      case SelectionType.AWAY_WIN:
        selectionText = away.TeamName;
        otherTeam = home.TeamName;
        selectedRank = away.ConferenceRank;
        opponentRank = home.ConferenceRank;
        break;
      case SelectionType.HOME_WIN:
        selectionText = home.TeamName;
        otherTeam = away.TeamName;
        selectedRank = home.ConferenceRank;
        opponentRank = away.ConferenceRank;
        break;
      case SelectionType.TIE:
        selectionText = 'tie';
        break;
    }
    if (selectionText && !isRandomize && self.IsAfterFirstLoad) {
      // analytics body (intentionally not sent)
      let _body = {
        gameIdentifier: _gameInput.gameGuid || _gameInput.gameScheduleNum,
        leagueGuid: self.config.leagueGuid,
        teamPicked: selectionText,
        teamPickedRank: selectedRank,
        weekNum: _gameInput.week,
        gameNum: _gameInput.gameScheduleNum,
        opponent: otherTeam,
        opponentRank: opponentRank,
        isPostSeason: !_gameInput.gameGuid,
      };
    }
    if (_gameInput.week < maxWeek + 1) {
      clearTimeout(self.Timeout);
      self.Timeout = setTimeout(() => {
        for (var i = maxWeek + 1; i <= maxWeek + 4; i++) {
          for (let weekObs of self.Weeks[i]) {
            for (let gameObs of weekObs.games()) {
              self.unselectGame(gameObs, true);
            }
          }
        }
        self.UpdateStats(-1, true);
        if (self.IsTeamSelected()) {
          self.SelectedTeam().Schedule(self.getScheduleForTeam(self.SelectedTeam()));
        }
        super.finishComputing();
      }, 500);
    } else {
      clearTimeout(self.Timeout);
      if (self.PostSeasonVersion == 1) {
        if (_gameInput.week + 1 == maxWeek + 2) {
          //dv
          for (let i = numGames + 4; i < self.GameArray.length; i++) {
            self.GameArray[i] = 0;
          }
        }
        if (_gameInput.week + 1 == maxWeek + 3) {
          //cg
          for (let i = numGames + 4 + 4; i < self.GameArray.length; i++) {
            self.GameArray[i] = 0;
          }
        }
        if (_gameInput.week + 1 == maxWeek + 4) {
          //sb
          self.GameArray[numGames + 4 + 4 + 2] = 0;
        }
      } else if (self.PostSeasonVersion == 2) {
        if (_gameInput.week + 1 == maxWeek + 2) {
          //dv
          for (let i = numGames + 6; i < self.GameArray.length; i++) {
            self.GameArray[i] = 0;
          }
        }
        if (_gameInput.week + 1 == maxWeek + 3) {
          //cg
          for (let i = numGames + 6 + 4; i < self.GameArray.length; i++) {
            self.GameArray[i] = 0;
          }
        }
        if (_gameInput.week + 1 == maxWeek + 4) {
          //sb
          self.GameArray[numGames + 6 + 4 + 2] = 0;
        }
      }
      for (var i = _gameInput.week + 1; i <= self.TotalWeeks; i++) {
        if (self.PostSeasonVersion == 1) {
          switch (i) {
            case maxWeek + 1:
              self.GameArray[numGames] = 0;
              self.GameArray[numGames + 1] = 0;
              self.GameArray[numGames + 2] = 0;
              self.GameArray[numGames + 3] = 0;
              break;
            case maxWeek + 2:
              self.GameArray[numGames + 4] = 0;
              self.GameArray[numGames + 5] = 0;
              self.GameArray[numGames + 6] = 0;
              self.GameArray[numGames + 7] = 0;
              break;
            case maxWeek + 3:
              self.GameArray[numGames + 8] = 0;
              self.GameArray[numGames + 9] = 0;
              break;
            case maxWeek + 4:
              self.GameArray[numGames + 10] = 0;
              break;
          }
        } else if (self.PostSeasonVersion == 2) {
          switch (i) {
            case maxWeek + 1:
              self.GameArray[numGames] = 0;
              self.GameArray[numGames + 1] = 0;
              self.GameArray[numGames + 2] = 0;
              self.GameArray[numGames + 3] = 0;
              self.GameArray[numGames + 4] = 0;
              self.GameArray[numGames + 5] = 0;
              self.GameArray[numGames + 6] = 0;
              break;
            case maxWeek + 2:
              self.GameArray[numGames + 7] = 0;
              self.GameArray[numGames + 8] = 0;
              self.GameArray[numGames + 9] = 0;
              self.GameArray[numGames + 10] = 0;
              break;
            case maxWeek + 3:
              self.GameArray[numGames + 11] = 0;
              self.GameArray[numGames + 12] = 0;
              break;
            case maxWeek + 4:
              self.GameArray[numGames + 13] = 0;
              break;
          }
        }

        this.Weeks[i][0].games([]);
        this.Weeks[i][1].games([]);
        this.Weeks[i][2].games([]);
      }
      self.updateSaveString();

      if (this.IsTeamSelected()) {
        if (_gameInput.week < maxWeek + 1) {
          this.UpdateStats(-1, false);
        } else {
          if (_gameInput.week == maxWeek + 1) {
            self.seedDV();
          }
          if (_gameInput.week == maxWeek + 2) {
            self.seedCG();
          }
          if (_gameInput.week == maxWeek + 3) {
            self.seedSB();
          }
        }
        this.SelectedTeam().Schedule(this.getScheduleForTeam(this.SelectedTeam()));
      }
      //DV
      if (_gameInput.week == maxWeek + 1) {
        self.seedDV();
      }
      //CG
      if (_gameInput.week == maxWeek + 2) {
        self.seedCG();
      }
      //SB
      if (_gameInput.week == maxWeek + 3) {
        self.seedSB();
      }
      this.UpdateStatsPostSeason();
    }
  }

  gameSelected(
    gameInput: KnockoutObservable<IGame>,
    selectedTeam: any,
    selectionType: SelectionType,
    positionCounter: number,
    skipUpdate?: boolean
  ): void {
    var self = this;
    if (selectionType == SelectionType.TIE) {
      gameInput().teamSelected('TIE');
    } else {
      gameInput().teamSelected(selectedTeam().TeamAbbreviation);
    }
    gameInput().selectionMade(true);
    gameInput().selectionType(selectionType);
    gameInput.valueHasMutated();
    if (!skipUpdate) {
      super.initTeamSubscribers();
      self.UpdateStats(positionCounter, positionCounter == self.NumGames() - 1);
    }
  }

  FillPostSeasonVersion1(
    DivisionName: string,
    fillWildCard: boolean,
    Position: number
  ): void {
    let self = this;
    let maxWeek = self.MaxWeek();
    let numGames = self.NumGames();
    var Teams: KnockoutObservable<ITeam>[] = [];
    var teamsInDivision = this.Dict[DivisionName];
    if (Position > numGames + 4) {
      fillWildCard = false;
    }
    if (fillWildCard || Position == -1) {
      for (var _Division in teamsInDivision) {
        var Division = teamsInDivision[_Division];
        for (var _team in Division) {
          var team = Division[_team];
          team().Tiebreakers({
            HTH: [],
            Sweeps: [],
            DivHTH: [],
          });
          Teams.push(team);
        }
      }
      var teamsSorted: KnockoutObservable<ITeam>[] = [];
      var divRanked = this.RankDivisions(
        Enumerable.From(Teams)
          .Where((p: any) => p().Conference == DivisionName)
          .GroupBy((p: any) => p().Division)
          .ToArray()
      );
      var topTeams = this.ConferenceRanker(
        [divRanked.North[0], divRanked.East[0], divRanked.South[0], divRanked.West[0]],
        true
      );
      if (topTeams && topTeams.length > 0) {
        topTeams = this.WildcardRanker(divRanked as any, topTeams, [
          divRanked.North.slice(1),
          divRanked.East.slice(1),
          divRanked.South.slice(1),
          divRanked.West.slice(1),
        ] as any);
        teamsSorted = this.RankRestOfConference(topTeams, Teams);
        topTeams = Enumerable.From(topTeams)
          .Where((p: any) => p != undefined)
          .ToArray();

        ko.utils.arrayForEach(
          Enumerable.From(topTeams).Take(4).ToArray(),
          (team: KnockoutObservable<NflTeam>) => {
            if (team && !this.IsDraftOrderRanking()) {
              switch (team().RankMethod()) {
                case RankMethod.AllPCT:
                  team().RankMethod(RankMethod.DivTop_AllPCT);
                  break;
                case RankMethod.ConfPCT:
                  team().RankMethod(RankMethod.DivTop_ConfPCT);
                  break;
                case RankMethod.DivPCT:
                  team().RankMethod(RankMethod.DivTop_DivPCT);
                  break;
                case RankMethod.SOS:
                  team().RankMethod(RankMethod.DivTop_SOS);
                  break;
                case RankMethod.SOV:
                  team().RankMethod(RankMethod.DivTop_SOV);
                  break;
                case RankMethod.HTH:
                  team().RankMethod(RankMethod.DivTop_HTH);
                  break;
                case RankMethod.CommonPct:
                  team().RankMethod(RankMethod.DivTop_CommonPct);
                  break;
              }
              ko.utils.arrayRemoveItem(teamsSorted, team);
            }
          }
        );
        ko.utils.arrayForEach(
          Enumerable.From(topTeams).Skip(4).Take(2).ToArray(),
          (team: KnockoutObservable<NflTeam>) => {
            ko.utils.arrayRemoveItem(teamsSorted, team);
          }
        );
        this.SkipDivisionRank(true);

        ko.utils.arrayForEach(
          topTeams.reverse(),
          (team: KnockoutObservable<NflTeam>, _idx) => {
            teamsSorted.unshift(team);
          }
        );
        teamsSorted = Enumerable.From(teamsSorted)
          .Where((p: any) => p != undefined)
          .ToArray();
        ko.utils.arrayForEach(
          teamsSorted,
          (team: KnockoutObservable<NflTeam>, index: number) => {
            team().ConferenceRank(index + 1);
          }
        );

        if (DivisionName == Conferences.AFC) {
          this.AFCTop(teamsSorted.slice(0, 6));
          if (fillWildCard) {
            var wc1 = ko.observable(
              new NflGame(this.AFCTop()[3], this.AFCTop()[4], maxWeek + 1)
            );
            var wc2 = ko.observable(
              new NflGame(this.AFCTop()[2], this.AFCTop()[5], maxWeek + 1)
            );

            wc1().gameScheduleNum(numGames);
            wc2().gameScheduleNum(numGames + 1);
            this.GameScheduleGameMap[numGames] = wc1;
            this.GameScheduleGameMap[numGames + 1] = wc2;

            self.GameArray[numGames] = 0;
            self.GameArray[numGames + 1] = 0;
            this.Weeks[maxWeek + 1][0].games([wc1, wc2]);
            this.Weeks[maxWeek + 1][0].games.valueHasMutated();
            this.Weeks[maxWeek + 2][0].games([]);
            this.Weeks[maxWeek + 3][0].games([]);
            this.Weeks[self.TotalWeeks][1].games([]);
          }
        } else {
          this.NFCTop(teamsSorted.slice(0, 6));
          if (fillWildCard) {
            var wc3 = ko.observable(
              new NflGame(this.NFCTop()[3], this.NFCTop()[4], maxWeek + 1)
            );
            var wc4 = ko.observable(
              new NflGame(this.NFCTop()[2], this.NFCTop()[5], maxWeek + 1)
            );
            wc3().gameScheduleNum(numGames + 2);
            wc4().gameScheduleNum(numGames + 3);
            this.GameScheduleGameMap[numGames + 2] = wc3;
            this.GameScheduleGameMap[numGames + 3] = wc4;

            self.GameArray[numGames + 2] = 0;
            self.GameArray[numGames + 3] = 0;
            this.Weeks[maxWeek + 1][2].games([wc3, wc4]);
            this.Weeks[maxWeek + 1][2].games.valueHasMutated();
            this.Weeks[maxWeek + 2][2].games([]);
            this.Weeks[maxWeek + 3][2].games([]);
            this.Weeks[self.TotalWeeks][1].games([]);
          }
        }
      }
    }
  }

  FillPostSeasonVersion2(
    DivisionName: string,
    fillWildCard: boolean,
    Position: number
  ): void {
    let self = this;
    let maxWeek = self.MaxWeek();
    let numGames = self.NumGames();
    var Teams: KnockoutObservable<ITeam>[] = [];
    var teamsInDivision = this.Dict[DivisionName];
    if (Position > numGames + 6) {
      fillWildCard = false;
    }
    if (fillWildCard || Position == -1) {
      for (var _Division in teamsInDivision) {
        var Division = teamsInDivision[_Division];
        for (var _team in Division) {
          var team = Division[_team];
          team().Tiebreakers({
            HTH: [],
            Sweeps: [],
            DivHTH: [],
          });
          Teams.push(team);
        }
      }
      var teamsSorted: KnockoutObservable<ITeam>[] = [];
      var divRanked = this.RankDivisions(
        Enumerable.From(Teams)
          .Where((p: any) => p().Conference == DivisionName)
          .GroupBy((p: any) => p().Division)
          .ToArray()
      );
      var topTeams = this.ConferenceRanker(
        [divRanked.North[0], divRanked.East[0], divRanked.South[0], divRanked.West[0]],
        true
      );
      if (topTeams && topTeams.length > 0) {
        topTeams = this.WildcardRanker(divRanked as any, topTeams, [
          divRanked.North.slice(1),
          divRanked.East.slice(1),
          divRanked.South.slice(1),
          divRanked.West.slice(1),
        ] as any);
        teamsSorted = this.RankRestOfConference(topTeams, Teams);
        topTeams = Enumerable.From(topTeams)
          .Where((p: any) => p != undefined)
          .ToArray();

        ko.utils.arrayForEach(
          Enumerable.From(topTeams).Take(4).ToArray(),
          (team: KnockoutObservable<NflTeam>) => {
            if (team && !this.IsDraftOrderRanking()) {
              switch (team().RankMethod()) {
                case RankMethod.AllPCT:
                  team().RankMethod(RankMethod.DivTop_AllPCT);
                  break;
                case RankMethod.ConfPCT:
                  team().RankMethod(RankMethod.DivTop_ConfPCT);
                  break;
                case RankMethod.DivPCT:
                  team().RankMethod(RankMethod.DivTop_DivPCT);
                  break;
                case RankMethod.SOS:
                  team().RankMethod(RankMethod.DivTop_SOS);
                  break;
                case RankMethod.SOV:
                  team().RankMethod(RankMethod.DivTop_SOV);
                  break;
                case RankMethod.HTH:
                  team().RankMethod(RankMethod.DivTop_HTH);
                  break;
                case RankMethod.CommonPct:
                  team().RankMethod(RankMethod.DivTop_CommonPct);
                  break;
              }
              ko.utils.arrayRemoveItem(teamsSorted, team);
            }
          }
        );
        ko.utils.arrayForEach(
          Enumerable.From(topTeams).Skip(4).Take(3).ToArray(),
          (team: KnockoutObservable<NflTeam>) => {
            ko.utils.arrayRemoveItem(teamsSorted, team);
          }
        );
        this.SkipDivisionRank(true);

        ko.utils.arrayForEach(
          topTeams.reverse(),
          (team: KnockoutObservable<NflTeam>, _idx) => {
            teamsSorted.unshift(team);
          }
        );
        teamsSorted = Enumerable.From(teamsSorted)
          .Where((p: any) => p != undefined)
          .ToArray();
        ko.utils.arrayForEach(
          teamsSorted,
          (team: KnockoutObservable<NflTeam>, index: number) => {
            team().ConferenceRank(index + 1);
          }
        );

        if (DivisionName == Conferences.AFC) {
          this.AFCTop(teamsSorted.slice(0, 7));
          if (fillWildCard) {
            let wc1 = ko.observable(
              new NflGame(this.AFCTop()[3], this.AFCTop()[4], maxWeek + 1)
            );
            let wc2 = ko.observable(
              new NflGame(this.AFCTop()[2], this.AFCTop()[5], maxWeek + 1)
            );
            let wc3 = ko.observable(
              new NflGame(this.AFCTop()[1], this.AFCTop()[6], maxWeek + 1)
            );

            wc1().gameScheduleNum(numGames);
            wc2().gameScheduleNum(numGames + 1);
            wc3().gameScheduleNum(numGames + 2);
            this.GameScheduleGameMap[numGames] = wc1;
            this.GameScheduleGameMap[numGames + 1] = wc2;
            this.GameScheduleGameMap[numGames + 2] = wc3;

            self.GameArray[numGames] = 0;
            self.GameArray[numGames + 1] = 0;
            self.GameArray[numGames + 2] = 0;
            this.Weeks[maxWeek + 1][0].games([wc1, wc2, wc3]);
            this.Weeks[maxWeek + 1][0].games.valueHasMutated();
            this.Weeks[maxWeek + 2][0].games([]);
            this.Weeks[maxWeek + 3][0].games([]);
            this.Weeks[self.TotalWeeks][1].games([]);
          }
        } else {
          this.NFCTop(teamsSorted.slice(0, 7));
          if (fillWildCard) {
            let wc3 = ko.observable(
              new NflGame(this.NFCTop()[3], this.NFCTop()[4], maxWeek + 1)
            );
            let wc4 = ko.observable(
              new NflGame(this.NFCTop()[2], this.NFCTop()[5], maxWeek + 1)
            );
            var wc5 = ko.observable(
              new NflGame(this.NFCTop()[1], this.NFCTop()[6], maxWeek + 1)
            );

            wc3().gameScheduleNum(numGames + 3);
            wc4().gameScheduleNum(numGames + 4);
            wc5().gameScheduleNum(numGames + 5);
            this.GameScheduleGameMap[numGames + 3] = wc3;
            this.GameScheduleGameMap[numGames + 4] = wc4;
            this.GameScheduleGameMap[numGames + 5] = wc5;

            self.GameArray[numGames + 3] = 0;
            self.GameArray[numGames + 4] = 0;
            self.GameArray[numGames + 5] = 0;
            this.Weeks[maxWeek + 1][2].games([wc3, wc4, wc5]);
            this.Weeks[maxWeek + 1][2].games.valueHasMutated();
            this.Weeks[maxWeek + 2][2].games([]);
            this.Weeks[maxWeek + 3][2].games([]);
            this.Weeks[self.TotalWeeks][1].games([]);
          }
        }
      }
    }
  }

  UpdateRankings(DivisionName: string, fillWildCard: boolean, Position: number): void {
    var self = this;
    if (self.PostSeasonVersion == 1) {
      self.FillPostSeasonVersion1(DivisionName, fillWildCard, Position);
    } else if (self.PostSeasonVersion == 2) {
      self.FillPostSeasonVersion2(DivisionName, fillWildCard, Position);
    }
  }

  RankDivisions(Divisions: any): {
    North: KnockoutObservable<ITeam>[];
    East: KnockoutObservable<ITeam>[];
    South: KnockoutObservable<ITeam>[];
    West: KnockoutObservable<ITeam>[];
    [key: string]: KnockoutObservable<ITeam>[];
  } {
    var topTeams: {
      North: KnockoutObservable<ITeam>[];
      East: KnockoutObservable<ITeam>[];
      South: KnockoutObservable<ITeam>[];
      West: KnockoutObservable<ITeam>[];
      [key: string]: KnockoutObservable<ITeam>[];
    } = { North: [], East: [], South: [], West: [] };
    for (var Division of Divisions) {
      for (var team of Division.source) {
        team().DraftOrderRankMethod(null);
        team().RankMethod(-1);
      }
      for (var i = 0; i < 4; i++) {
        var top = this.DivisionPickTop(Division.source, false);
        if (top) {
          Division.source.splice(Division.source.indexOf(top), 1);
          topTeams[top().Division].push(top);
        }
      }
    }

    return topTeams;
  }

  private getNflTiebreakerOptions(): NflTiebreakerOptions | undefined {
    if (!this.tiebreakerRuleSet) {
      return undefined;
    }

    return {
      tiebreakerRuleSet: this.tiebreakerRuleSet,
    };
  }

  DivisionPickTop(
    teamsInADivision: KnockoutObservable<ITeam>[],
    _isWildcard: boolean
  ): KnockoutObservable<ITeam> {
    var top = this.BestPctNaN(teamsInADivision);
    if (top.length == 1) {
      return top[0];
    }

    // Delegate to extracted NFL division tiebreaker service.
    const teamObsMap = buildTeamObsMap(top);
    const plainTeams: NflTiebreakerTeam[] = top.map(toNflTiebreakerTeam);
    const gamesLookup = this.buildNflGamesLookup(top);
    const result = nflDivisionRanker(plainTeams, gamesLookup, this.getNflTiebreakerOptions());
    applyTiebreakerResult(result, teamObsMap, {
      preventRankOverride: this.IsDraftOrderRanking(),
    });
    return teamObsMap.get(result.orderedTeams[0].teamAbbreviation)!;
  }




  // DivisionTieBreaker and HtHDivision removed — superseded by nflDivisionRanker
  // wired directly in DivisionPickTop above.

  WildcardRanker(
    _Divisions: { key: string; value: unknown }[],
    top: KnockoutObservable<ITeam>[],
    teams: KnockoutObservable<ITeam>[]
  ): KnockoutObservable<ITeam>[] {
    if (teams[0] && (teams[0] as any).length) {
      teams = [].concat.apply([], teams);
    }
    // Flatten nested per-division slices into a single wildcard pool, then
    // rank the full pool via the extracted conference ranker and take the top two.
    const teamObsMap = buildTeamObsMap(teams);
    const plainTeams: NflTiebreakerTeam[] = teams.map(toNflTiebreakerTeam);
    const gamesLookup = this.buildNflGamesLookup(teams);
    const result = nflWildcardRanker(plainTeams, gamesLookup, this.getNflTiebreakerOptions());
    applyTiebreakerResult(result, teamObsMap);
    const ranked = reorderTeamObs(result, teamObsMap);
    top.push(ranked[0], ranked[1]);
    return top;
  }

  RankRestOfConference(
    topTeams: KnockoutObservable<ITeam>[],
    teams: KnockoutObservable<ITeam>[]
  ): KnockoutObservable<ITeam>[] {
    var top = [];
    var remaining = teams.slice(0);
    for (var i = 0; i < topTeams.length; i++) {
      top.push(topTeams[i]);
      remaining.splice(remaining.indexOf(topTeams[i]), 1);
    }
    for (var team of remaining) {
      if (!this.IsDraftOrderRanking()) {
        team().RankMethod(-1);
      }
    }
    while (remaining.length > 0) {
      const isWildcardPick = top.length < 7 || this.TreatNonPlayoffTeamsLikeWildcards();
      var best = this.ConferencePickTop(
        remaining,
        null,
        isWildcardPick
      );
      top.push(best);
      remaining.splice(remaining.indexOf(best), 1);
    }
    return top;
  }

  /**
   * Build a GameResultsLookup from the schedules of the given NFL team observables.
   * Shared by all tiebreaker entry-point wrappers. Dedupes by gameGuid (or derived key).
   * NFL uses per-team schedules rather than the Weeks structure the shared
   * extractGamesFromWeeks helper consumes, so the extraction stays inline here.
   */
  private buildNflGamesLookup(teams: KnockoutObservable<ITeam>[]): ArrayGameResultsLookup {
    const gameResults: GameResult[] = [];
    const seenGames = new Set<string>();
    for (const obs of teams) {
      const schedule = this.getScheduleForTeam(obs());
      for (const gameObs of schedule) {
        if (!gameObs) continue;
        const game = gameObs();
        if (!game || !game.selectionMade?.()) continue;
        const home = game.home?.();
        const away = game.away?.();
        if (!home || !away) continue;
        const gameKey = `${(game as any).gameGuid ?? `${home.TeamAbbreviation}-${away.TeamAbbreviation}-${game.SortOrder?.()}`}`;
        if (seenGames.has(gameKey)) continue;
        seenGames.add(gameKey);
        const winner = game.TeamThatWon?.()?.()?.TeamAbbreviation ?? null;
        gameResults.push({
          homeTeam: home.TeamAbbreviation,
          awayTeam: away.TeamAbbreviation,
          winner,
          selectionType: game.selectionType?.() ?? 0,
        });
      }
    }
    return new ArrayGameResultsLookup(gameResults);
  }

  ConferenceRanker(
    teams: KnockoutObservable<ITeam>[],
    preventRankOverride?: boolean
  ): KnockoutObservable<ITeam>[] {
    if (Enumerable.From(teams).Any((p: any) => p == undefined)) {
      return;
    }

    const teamObsMap = buildTeamObsMap(teams);
    const plainTeams: NflTiebreakerTeam[] = teams.map(toNflTiebreakerTeam);
    const gamesLookup = this.buildNflGamesLookup(teams);

    // Divisions present in the incoming team set (one per division at the
    // standard call site: the four division winners of a conference).
    const divisions = Array.from(new Set(plainTeams.map(t => t.division)));

    const result = nflConferenceRanker(plainTeams, gamesLookup, divisions, this.getNflTiebreakerOptions());
    applyTiebreakerResult(result, teamObsMap, { preventRankOverride });
    return reorderTeamObs(result, teamObsMap);
  }

  ConferencePickTop(
    teams: KnockoutObservable<ITeam>[],
    preventRankOverride?: boolean,
    _isWildcard?: boolean
  ): KnockoutObservable<ITeam> {
    if (Enumerable.From(teams).Any((p: any) => p == undefined)) return;

    var top = this.BestPctNaN(teams);
    if (top.length == 1) {
      if (
        (top[0] as any).SkipRanking === false ||
        top[0]().RankMethod() == RankMethod.Unknown ||
        (top[0]().RankMethod() <= 10 &&
          top[0]().RankMethod() >= 5 &&
          preventRankOverride !== true)
      ) {
        if (!this.IsDraftOrderRanking()) {
          top[0]().RankMethod(RankMethod.AllPCT);
        }
      }
      return top[0];
    }

    // Multiple teams tied at overall pctNaN — delegate to the extracted NFL
    // conference tiebreaker service. The service's nflConferenceRanker handles
    // the same-division-subgroup → conference-steps split that the legacy
    // ConfTiebreaker implemented inline.
    const teamObsMap = buildTeamObsMap(top);
    const plainTeams: NflTiebreakerTeam[] = top.map(toNflTiebreakerTeam);
    const gamesLookup = this.buildNflGamesLookup(top);
    const result = nflConferencePickTopResult(plainTeams, gamesLookup, _isWildcard === true, this.getNflTiebreakerOptions());
    applyTiebreakerResult(result, teamObsMap, {
      preventRankOverride: this.IsDraftOrderRanking(),
    });
    return teamObsMap.get(result.orderedTeams[0].teamAbbreviation)!;
  }



  // ConfTiebreaker removed — superseded by nflConferenceRanker wired directly
  // in ConferencePickTop above.




  // HtHConference removed — superseded by h2hConfStep in nfl-steps.ts.


  // BestCommonPct removed — superseded by createCommonGamesStep in nfl-steps.ts.


  UpdateStats(Position: number, fillWildcard?: boolean): void {
    var self = this;
    self.updateSaveString();
    self.setSOSandSOV();
    self.UpdateRankings(Conferences.AFC, fillWildcard, Position);
    self.UpdateRankings(Conferences.NFC, fillWildcard, Position);
    self.pushHistory();
    document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));

    super.finishComputing();
  }

  UpdateStatsPostSeason(): void {
    var self = this;
    self.updateSaveString();
    self.pushHistory();
    document.querySelectorAll('#content .hidden').forEach(el => el.classList.remove('hidden'));

    super.finishComputing();
  }

  setSOSandSOV(): void {
    var self = this;
    for (var i = 0; i < self.AllTeams().length; i++) {
      var team: NflTeam = self.AllTeams()[i]() as NflTeam;
      var SOS = { Wins: 0, Losses: 0, Ties: 0 };
      var SOV = { Wins: 0, Losses: 0, Ties: 0 };
      team.Schedule(self.getScheduleForTeam(team));
      ko.utils.arrayForEach(team.Schedule(), (gameObs: any, _idx) => {
        if (gameObs && gameObs().week <= self.MaxWeek()) {
          var game = ko.unwrap(gameObs);
          var SOVMultiplier = 0;
          var home = ko.unwrap(game.home);
          var away = ko.unwrap(game.away);
          var isHome = home.TeamNameObs() == team.TeamNameObs();
          if (game.selectionType() != SelectionType.NONE) {
            if (game.teamSelected() == team.TeamAbbreviation) {
              SOVMultiplier += 1;
            }
            if (isHome) {
              SOV.Wins += away.GameRecords.AllRecord.Wins() * SOVMultiplier;
              SOV.Losses += away.GameRecords.AllRecord.Losses() * SOVMultiplier;
              SOV.Ties += away.GameRecords.AllRecord.Ties() * SOVMultiplier;

              SOS.Wins += away.GameRecords.AllRecord.Wins();
              SOS.Losses += away.GameRecords.AllRecord.Losses();
              SOS.Ties += away.GameRecords.AllRecord.Ties();
            } else {
              SOV.Wins += home.GameRecords.AllRecord.Wins() * SOVMultiplier;
              SOV.Losses += home.GameRecords.AllRecord.Losses() * SOVMultiplier;
              SOV.Ties += home.GameRecords.AllRecord.Ties() * SOVMultiplier;

              SOS.Wins += home.GameRecords.AllRecord.Wins();
              SOS.Losses += home.GameRecords.AllRecord.Losses();
              SOS.Ties += home.GameRecords.AllRecord.Ties();
            }
          }
        }
      });
      team.SOS(self.calcWLT(SOS.Losses, SOS.Wins, SOS.Ties));
      team.SOV(self.calcWLT(SOV.Losses, SOV.Wins, SOV.Ties));
      (team.SOSObj as KnockoutObservable<unknown>)(SOS);
      (team.SOVObj as KnockoutObservable<unknown>)(SOV);
    }
  }

  override setCurrentSeason(reset: boolean): void {
    var self = this;
    super.setCurrentSeason(reset);
    self.DraftOrder([]);
    self.DraftOrder.valueHasMutated();
  }

  /**
   * Rank a segment of teams in draft order (worst = pick 1) using nflDraftOrder.
   * Sets DraftOrderRankMethod on each team. Returns KO observables worst-first.
   */
  private rankSegmentDraftOrder(
    segment: KnockoutObservable<ITeam>[]
  ): KnockoutObservable<ITeam>[] {
    if (segment.length === 0)
    {
      return [];
    }
    if (segment.length === 1)
    {
      // Alone in its segment — no tiebreaker fired, leave the label null.
      return segment;
    }
    const obsMap = buildTeamObsMap(segment);
    const plainTeams: NflTiebreakerTeam[] = segment.map(toNflTiebreakerTeam);
    const gamesLookup = this.buildNflGamesLookup(segment);
    const result = nflDraftOrder(plainTeams, gamesLookup);
    for (const [abbr, method] of result.rankMethods)
    {
      const obs = obsMap.get(abbr);
      if (obs)
      {
        (obs() as NflTeam).DraftOrderRankMethod(method);
      }
    }
    return reorderTeamObs(result, obsMap);
  }

  generateDraftOrder(wc: IWeek[], dv: IWeek[], cg: IWeek[], sb: IWeek[]): void {
    const self = this;
    const nonPlayoffTeams: KnockoutObservable<ITeam>[] = Enumerable.From(self.AllTeams())
      .Where((p: KnockoutObservable<NflTeam>) => p().ConferenceRank() > 7)
      .ToArray();

    // Reset draft-order rank methods before recomputing.
    for (const t of nonPlayoffTeams) (t() as NflTeam).DraftOrderRankMethod(null);

    const top: KnockoutObservable<ITeam>[] = [
      ...this.rankSegmentDraftOrder(nonPlayoffTeams),
      ...this.rankSegmentDraftOrder(self.getLoserFromWeekArray(wc)),
      ...this.rankSegmentDraftOrder(self.getLoserFromWeekArray(dv)),
      ...this.rankSegmentDraftOrder(self.getLoserFromWeekArray(cg)),
    ];

    if (sb[1].games().length == 1 && sb[1].games()[0]().selectionMade()) {
      top.push(sb[1].games()[0]().TeamThatLost());
      top.push(sb[1].games()[0]().TeamThatWon());
    }
    for (var i = 0; i < top.length; i++) {
      let team = ko.unwrap(top[i]);
      team.DraftOrderNumber(i + 1);
    }
    self.DraftOrder(top as KnockoutObservable<NflTeam>[]);
  }

  getLoserFromWeekArray(arr: IWeek[]): KnockoutObservable<ITeam>[] {
    var ret = [];
    if (arr[0].games().length || arr[2].games().length) {
      for (let game of arr[0].games()) {
        if (game().selectionMade()) {
          ret.push(game().TeamThatLost());
        }
      }

      for (let game of arr[2].games()) {
        if (game().selectionMade()) {
          ret.push(game().TeamThatLost());
        }
      }
    } else if (arr[1].games().length) {
      //sb
      for (let game of arr[1].games()) {
        if (game().selectionMade()) {
          ret.push(game().TeamThatLost());
          ret.push(game().TeamThatWon());
        }
      }
    }

    return ret;
  }


  private calcWLT(Losses: number, Wins: number, Ties: number): number {
    var denom = Wins + Losses + Ties;
    if (denom == 0) {
      return 0;
    }
    return (Wins + 0.5 * Ties) / denom;
  }

  hasDraftTrade(teamAbbreviation: string): ITeam | ITeamData {
    return this.DraftTradesMap[teamAbbreviation];
  }

  createMap(): void {
    var self = this;
    for (var weekidx in self.Weeks) {
      var week = self.Weeks[weekidx];
      for (let x of week) {
        for (var game of x.games()) {
          if (game && game().gameScheduleNum) {
            self.gameMap[game().gameGuid] = game().gameScheduleNum();
          }
        }
      }
    }
    let x: string[] = [];
    for (var item in this.gameMap) {
      x.push(
        'UPDATE games SET GameScheduleNum = ' +
          this.gameMap[item] +
          " WHERE GameGUID = '" +
          item +
          "'\n"
      );
    }
    console.log(x.join(''));
  }
}
